import React, { useContext, useEffect, useState } from 'react';

import { Plus } from 'lucide-react';

import { RouterContext } from 'common/containers/RouterContainer';
import { RoadmapFilterContext } from 'common/subdomain/admin/AdminRoadmap/AdminRoadmapContext';
import { findLastActiveFilter } from 'common/subdomain/admin/AdminRoadmap/AdminRoadmapViewSettingsModal/SavedViewUtils';
import { Select } from 'common/uiV3/Select';
import { findRoadmapByID } from 'common/util/roadmapUtils';

import type { Roadmap } from 'common/api/endpoints/roadmaps';
import type { Option } from 'common/ui/common/select/SelectCommon';

interface Props {
  onNewRoadmap: () => void;
  roadmap: Roadmap | null;
  roadmaps: Roadmap[];
}

const AdminRoadmapSelector = ({ onNewRoadmap, roadmap, roadmaps }: Props) => {
  const router = useContext(RouterContext);
  const { setViewDraft } = useContext(RoadmapFilterContext);

  // keep selected roadmap in local state to update UI immediately when switching roadmaps
  const [selectedRoadmap, setSelectedRoadmap] = useState<Option | undefined>(
    roadmap ? { value: roadmap._id, label: roadmap.name } : undefined,
  );

  // Update selectedRoadmap when roadmap changes (e.g., when name is updated)
  useEffect(() => {
    if (roadmap) {
      setSelectedRoadmap({ value: roadmap._id, label: roadmap.name });
    }
  }, [roadmap]);

  const options = roadmaps.map((roadmap) => ({
    value: roadmap._id,
    label: roadmap.name,
  }));

  const onChange = (roadmapID?: string) => {
    if (!roadmapID) {
      return;
    }

    const roadmap = findRoadmapByID(roadmaps, roadmapID);
    if (!roadmap) {
      return;
    }

    const isSameRoadmap = roadmap._id === selectedRoadmap?.value;
    if (isSameRoadmap) {
      return;
    }

    // update local selected roadmap for immediate UI update
    setSelectedRoadmap({ value: roadmapID, label: roadmap.name });

    // reset local view draft when switching roadmaps to reset the applied view
    const lastViewedFilter = findLastActiveFilter(roadmap.settings, roadmap.savedViews);
    const lastActiveViewID = roadmap?.settings?.lastActiveFilters?.viewID || undefined;
    const view = roadmap?.savedViews.find((savedView) => lastActiveViewID === savedView._id);
    if (view) {
      setViewDraft({
        filterQuery: view.filterQuery,
        hiddenColumnIDs: view.hiddenColumnIDs,
      });
    }

    router.replace(`/admin/roadmap/${roadmap.urlName}${lastViewedFilter ?? ''}`);
  };

  return (
    <div>
      <Select
        cta={{
          option: {
            label: (
              <Select.OptionLabel>
                <Plus /> Create new roadmap
              </Select.OptionLabel>
            ),
            value: 'newRoadmap',
          },
          onClick: onNewRoadmap,
        }}
        onValueChange={onChange}
        options={options}
        placeholder="Select roadmap"
        value={selectedRoadmap?.value}
        isSearchable={options.length > 5}
      />
    </div>
  );
};

export default AdminRoadmapSelector;
